import React, { useState, FC } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { usePropertyLookup } from '@/apps/runtimePropertyHealthScore/hooks/usePropertyLookup';
import BookDemoModal from '../../../features/bookDemo/components/BookDemoModal';
import useEmailSubscription from '../../../features/mailSubscription/hooks/useEmailSubscription';
import ConfirmEmailModal from '../../../features/mailSubscription/components/ConfirmEmailModal';
import { SubscriptionData } from '../../../features/mailSubscription/api/subscription';
import PropertySearch from '@/features/realtimeProperty/components/PropertySearch';
import { usePropertyOrDemoData } from '../../../features/demoProperty/hooks/usePropertyOrDemoData';
import { isDemoProperty } from '../../../features/demoProperty/utils/isDemoCheck';
import { useRealtimePropertyWithFilters } from '@/features/realtimeProperty/hooks/useRealtimePropertyWithFilters';
import propertyChartsResponseMock from '@/features/demoProperty/mocks/11080502/propertyChartsRealtimeResponse.json';
import { RealtimeChartDataResponse } from '@/features/realtimeProperty/api/fetchRealtimeProperty';
import NoAvailabilityMessage from '@/features/property/components/NoAvailabilityMessage';
import { usePropertyVisibility } from '@/features/property/hooks/usePropertyVisibility';
import { usePropertyFilterControls } from '@/features/realtimeProperty/hooks/useRealtimePropertyFilterControls';
import { usePropertyDataState } from '@/features/realtimeProperty/hooks/useRealtimePropertyDataState';
import LoadingSpinner from '@/designSystem/LoadingSpinner';
import ErrorMessage from '@/designSystem/Error';
import PropertyPerformance from '@/features/property/components/PropertyPerformance';
import { getCurrencySymbolFromCountryCode } from '@/utils';
import { DashboardLayout } from '@/features/layouts/components/DashboardLayout';
import { SimpleKPIMetrics } from '@/features/kpi/components/SimpleKPIMetrics';
import { FreeRecommendations } from '@/features/freeRecommendations/components/FreeRecommendations';
import { CompactMap } from '@/features/map/components/CompactMap';
import { MapModal } from '@/features/map/components/MapModal';
import { Share2, Download } from 'lucide-react';
import { Button } from '../../../designSystem/Button';
import { HealthScore } from '../../../features/healthScore/components/HealthScore';
import { ParametersAdjustedTooltip } from '../../../features/property/components/ParametersAdjustedAlert';
import { DemoModeTooltip } from '../../../features/demoProperty/components/DemoTooltip';
import { ShareModal } from '@/features/share/components/ShareModal';
import { exportFreeData } from '../../../features/share/utils/freeExporter';
import { DownloadModal } from '../../../features/share/components/DownloadModal';
import { usePropertyExport } from '../../../features/share/hooks/usePropertyExport';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/designSystem/Dialog';

interface RuntimeHealthScorePage {
  showEmailConfirmModal?: boolean;
}

const RuntimeHealthScorePage: FC<RuntimeHealthScorePage> = ({ showEmailConfirmModal }) => {
  const params = useParams<{ propertyId: string }>();
  const propertyId = parseInt(params.propertyId, 10);
  const navigate = useNavigate();
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const [showCharts, setShowCharts] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const propertyResponse = usePropertyOrDemoData(propertyId);
  const {
    shouldShowSubscriptionModal,
    subscribeEmail,
    isSubscribing,
    error: subscriptionError
  } = useEmailSubscription();

  const { isExporting, exportData } = usePropertyExport();
  const isDemo = isDemoProperty(propertyId);

  const {
    propertyChartResponse,
    filters,
    filterOptions,
    updateFilter,
    isParametersAdjusted,
  } = useRealtimePropertyWithFilters(propertyId, {
    useDemoData: isDemo,
    demoData: propertyChartsResponseMock as RealtimeChartDataResponse
  });

  const onSelectProperty = (selectedPropertyId: number) => {
    navigate(`../property/${selectedPropertyId}`)
  }

  const {
    recentLookups,
    handleSelectProperty,
    handleSelectRecentLookup
  } = usePropertyLookup(onSelectProperty);

  const handleConsultationClick = () => {
    setShowConsultationModal(true);
  };

  const handleEmailSubscription = async (data: SubscriptionData) => {
    await subscribeEmail(data);
  };

  const handleExportData = async () => {
    if (!propertyResponse.data || !propertyChartResponse.data) return;

    await exportData(
      exportFreeData,
      {
        chartData: propertyChartResponse.data,
        mainProperty: propertyResponse.data
      },
      {
        filename: `property-analysis-${propertyResponse.data.name.toLowerCase().replace(/\s+/g, '-')}`
      }
    );
  };

  const handleShareClick = () => {
    setShowShareModal(true);
  };

  const handleDownloadClick = () => {
    setShowDownloadModal(true);
  };

  const { visiblePropertyIds, toggleAllProperties } = usePropertyVisibility();
  const filterControls = usePropertyFilterControls(filters, filterOptions, updateFilter);

  const {
    isLoading,
    isError,
    marketProperties,
    hasNoData
  } = usePropertyDataState(
    propertyResponse,
    propertyChartResponse,
    toggleAllProperties
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return (
      <div className='flex flex-col gap-4 p-6'>
        <PropertySearch onSelect={handleSelectProperty} />
        <ErrorMessage />
      </div>
    );
  }

  if (hasNoData) {
    return (
      <div className='flex flex-col gap-4 p-6'>
        <PropertySearch onSelect={handleSelectProperty} />
        <NoAvailabilityMessage filterControls={filterControls} />
      </div>
    );
  }

  const searchAndFiltersComponent = (
    <div className="flex flex-wrap items-center justify-between gap-4">
      {/* Left: Search + Filters + Compact Alert + Demo Badge */}
      <div className="flex items-center gap-3 flex-1 flex-wrap">
        <div className="flex-shrink-0">
          <PropertySearch onSelect={handleSelectProperty} />
        </div>
        {filterControls}
        {/* Compact Tooltips */}
        {isParametersAdjusted && <ParametersAdjustedTooltip />}
        {isDemoProperty(propertyId) && <DemoModeTooltip />}
        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Live Data</span>
      </div>

      {/* Right: Export Action Buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button 
          variant='link' 
          onClick={handleShareClick}
          disabled={!propertyResponse.data}
        >
          <Share2 className="w-4 h-4 mr-1" />
          Share
        </Button>
        <Button 
          variant='link' 
          onClick={handleDownloadClick}
          disabled={!propertyResponse.data}
        >
          <Download className="w-4 h-4 mr-1" />
          Download
        </Button>
      </div>
    </div>
  );

  const currencySymbol = propertyResponse.data?.country_code ? getCurrencySymbolFromCountryCode(propertyResponse.data.country_code) : ''

  // Render the dashboard layout
  return (
    <>
      {/* Add id to the main container for PDF capture */}
      <div id="dashboard-content">
        <DashboardLayout
          stickyControls={{
            searchAndFiltersComponent,
          }}
          kpiMetricsContent={
            propertyResponse.data && propertyChartResponse.data ? (
              <SimpleKPIMetrics
                propertyChartResponse={propertyChartResponse}
                competitors={propertyChartResponse.data.competitors}
                currencySymbol={currencySymbol}
              />
            ) : null
          }
          healthScoreContent={
            propertyResponse.data && propertyChartResponse.data ? (
              <HealthScore
                metrics={propertyChartResponse.data.property.data_points}
                propertyId={propertyResponse.data.property_id}
                onViewDetailsClick={() => setShowCharts(true)}
                propertyTitle={propertyResponse.data.name}
                propertyDatapoints={propertyChartResponse.data.property.data_points}
                currencySymbol={currencySymbol}
              />
            ) : null
          }
          mapContent={
            propertyResponse.data && propertyChartResponse.data ? (
              <CompactMap
                ownProperty={propertyChartResponse.data.property}
                competitors={propertyChartResponse.data.competitors}
                marketProperties={marketProperties}
                onExpandClick={() => setShowMapModal(true)}
              />
            ) : null
          }
          recommendationsContent={
            propertyResponse.data && propertyChartResponse.data ? (
              <FreeRecommendations
                property={propertyChartResponse.data.property}
                competitors={propertyChartResponse.data.competitors}
                currencySymbol={currencySymbol}
                onConsultationClick={handleConsultationClick}
                showLeadGenElements={!isDemoProperty(propertyId)}
              />
            ) : null
          }
        />
      </div>

      {/* Share Modal */}
      {propertyResponse.data && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          propertyName={propertyResponse.data.name}
          propertyId={propertyResponse.data.property_id}
        />
      )}

      {/* Download Modal */}
      {propertyResponse.data && (
        <DownloadModal
          isOpen={showDownloadModal}
          onClose={() => setShowDownloadModal(false)}
          propertyName={propertyResponse.data.name}
          onDownloadExcel={handleExportData}
          isExcelExporting={isExporting}
        />
      )}

      <BookDemoModal
        isOpen={showConsultationModal}
        onClose={() => setShowConsultationModal(false)}
      />

      {/* Map Modal */}
      {propertyResponse.data && propertyChartResponse.data && (
        <MapModal
          isOpen={showMapModal}
          onClose={() => setShowMapModal(false)}
          ownProperty={propertyChartResponse.data.property}
          competitors={propertyChartResponse.data.competitors}
          marketProperties={marketProperties}
        />
      )}

      {!isDemoProperty(propertyId) && showEmailConfirmModal && (
        <ConfirmEmailModal
          isOpen={shouldShowSubscriptionModal}
          onSubmit={handleEmailSubscription}
          isSubmitting={isSubscribing}
          submissionError={subscriptionError}
        />
      )}

      <Dialog open={showCharts} onOpenChange={setShowCharts}>
        <DialogContent className="max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Performance Charts</DialogTitle>
          </DialogHeader>
          
          {propertyResponse.data && propertyChartResponse.data && (
            <PropertyPerformance
              competitorMetrics={propertyChartResponse.data?.competitors}
              propertyDatapoints={propertyChartResponse.data.property.data_points || []}
              visiblePropertyIds={visiblePropertyIds}
              countryCode={propertyResponse.data.country_code}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RuntimeHealthScorePage;